import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Kanban, PlusCircle, BarChart3, 
  DollarSign, Zap, LogOut, Settings
} from 'lucide-react';
import { useAuth } from './AuthContext';

const NAV_ITEMS = [
  { path: '/crm', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/crm/pipeline', label: 'Pipeline', icon: Kanban },
  { path: '/crm/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/analytics', label: 'Apply Funnel', icon: BarChart3 },
  { path: '/admin/home-loan-referrals', label: 'Home Loan Referrals', icon: BarChart3 },
  { path: '/crm/commissions', label: 'Commissions', icon: DollarSign },
  { path: '/crm/deals/new', label: 'New Deal', icon: PlusCircle },
  { path: '/crm/settings', label: 'Settings', icon: Settings },
];

export default function CRMLayout({ children, alertCount = 0 }) {
  const location = useLocation();
  const { logout } = useAuth();

  return (
    <div className="min-h-screen bg-navy-900 flex">
      {/* Sidebar */}
      <aside className="w-16 lg:w-56 bg-navy-950 border-r border-white/5 flex flex-col fixed h-full z-20" data-testid="crm-sidebar">
        <div className="p-3 lg:p-4 border-b border-white/5">
          <Link to="/crm" className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-purple-400 flex-shrink-0" />
            <span className="hidden lg:block text-white font-heading text-sm uppercase tracking-wider">FlashIQ</span>
          </Link>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-2">
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path || 
              (path === '/crm/pipeline' && location.pathname.startsWith('/crm/deals/')) ||
              (path === '/admin/analytics' && location.pathname.startsWith('/admin/analytics')) ||
              (path === '/admin/home-loan-referrals' && location.pathname.startsWith('/admin/home-loan-referrals'));
            return (
              <Link
                key={path}
                to={path}
                data-testid={`crm-nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive 
                    ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden lg:block">{label}</span>
                {label === 'Dashboard' && alertCount > 0 && (
                  <span className="ml-auto hidden lg:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold">
                    {alertCount > 9 ? '9+' : alertCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-2">
          <Link to="/" className="text-gray-500 hover:text-gray-300 text-xs hidden lg:block">
            Back to Website
          </Link>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-gray-500 hover:text-red-400 text-xs transition-colors w-full"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden lg:block">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-16 lg:ml-56">
        {children}
      </main>
    </div>
  );
}
